import { IconCheck, IconX } from "@tabler/icons-react"
import { type FC } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import LoadingIndicator from "~/components/LoadingIndicator"
import { query } from "~/utilities/trpc"

const steps = [
	{ id: "queued", label: "Queued", description: "Your request is in the queue" },
	{ id: "generating", label: "Generating chapter", description: "Writing content for the next chapter" },
	{ id: "completed", label: "Completed", description: "Your chapter is ready!" },
]

const ChapterGenerationStatus: FC<{ requestId: number }> = ({ requestId }) => {
	const { data, error } = query.ai.getChapterStatus.useQuery(requestId, {
		refetchInterval: query =>
			query.state.data !== null &&
			query.state.data?.status !== "completed" &&
			query.state.data?.status !== "error"
				? 3000
				: false,
	})

	const getCurrentStepIndex = () => {
		if (error || data?.status === "error") return -1
		if (data === null || data === undefined) return 0
		return steps.findIndex(step => step.id === data.status)
	}

	const currentStepIndex = getCurrentStepIndex()
	const isError = error || data?.status === "error"

	if (data === null && !error) {
		return (
			<div className="flex flex-col items-center justify-center gap-4 py-8">
				<div className="text-sm text-gray-500">Not found</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-6">
			{isError ? (
				<div className="flex items-center gap-3">
					<div className="flex size-10 items-center justify-center rounded-full bg-red-100">
						<IconX className="size-6 text-red-600" />
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-lg font-semibold text-gray-900">Error generating chapter</span>
						<span className="text-sm text-gray-600">Something went wrong. Please try again.</span>
					</div>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{steps.map((step, index) => {
						const isCompleted =
							index < currentStepIndex || (index === currentStepIndex && data?.status === "completed")
						const isCurrent = index === currentStepIndex && data?.status !== "completed"

						return (
							<div key={step.id} className="flex gap-4">
								<div className="flex flex-col items-center">
									<div
										className={cn(
											"flex size-10 items-center justify-center rounded-full bg-gray-100 transition-colors",
											isCurrent && "bg-primary/10",
											isCompleted && "bg-green-100",
										)}
									>
										{isCompleted ? (
											<IconCheck className="size-5 text-green-600" />
										) : isCurrent ? (
											<LoadingIndicator size={24} />
										) : (
											<div className="size-2 rounded-full bg-gray-300" />
										)}
									</div>
									{index < steps.length - 1 && (
										<div
											className={cn(
												"mt-2 h-12 w-0.5 bg-gray-200 transition-colors",
												isCompleted && "bg-green-500",
											)}
										/>
									)}
								</div>
								<div className="flex flex-1 flex-col gap-1 pb-6">
									<span
										className={cn(
											"text-base font-medium text-gray-400",
											isCurrent && "text-gray-900",
											isCompleted && "text-gray-700",
										)}
									>
										{step.label}
									</span>
									{isCurrent && <span className="text-sm text-gray-600">{step.description}</span>}
								</div>
							</div>
						)
					})}
				</div>
			)}
			{!isError && data?.status === "completed" && <AiGenerationFeedback id={requestId} type="chapter" />}
		</div>
	)
}

export default ChapterGenerationStatus
